import { useState, useMemo } from "react";
import { Search, MapPin, Clock, X } from "lucide-react";
import { searchLocations } from "../../data/galiciaLocations";
import { useRecentSearches } from "../../hooks/useRecentSearches";

// Panel "Onde" do SearchBar do header.
// Antes tiña unha lista hardcoded de só 10 comarcas. Agora utiliza o dataset
// completo de Galicia (4 provincias + 53 comarcas + 313 concellos = ~370).

export default function LocationPanel({ value, onChange, onClose }) {
  const [query, setQuery] = useState(value || "");
  const { recent, remove: removeRecent, clear: clearRecent } = useRecentSearches();

  const results = useMemo(() => searchLocations(query, 30), [query]);
  const showRecents = !query.trim() && recent.length > 0;

  function selectRecent(term) {
    onChange(term);
    onClose();
  }

  function handleSelect(loc) {
    // Garda o label como string (compatible co valor existente).
    onChange(loc.label);
    onClose();
  }

  function handleInputChange(e) {
    setQuery(e.target.value);
    onChange(e.target.value);
  }

  const colorByType = (type) =>
    type === "provincia" ? "text-blue-600" :
    type === "comarca"   ? "text-amber-600" :
                           "text-muted";

  return (
    <div className="absolute top-full mt-2 bg-white rounded-2xl shadow-card-hover border border-border p-4 z-50 w-80 animate-fade-in">
      {/* Search input */}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
        />
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          placeholder="Provincia, comarca ou concello…"
          className="airbnb-input pl-9 w-full"
          autoFocus
        />
      </div>

      {/* X-4.4 · Búsquedas recentes (mostradas só con query baleiro) */}
      {showRecents && (
        <div className="mb-3">
          <div className="flex items-center justify-between px-1 mb-1">
            <span className="text-xs font-medium text-muted uppercase tracking-wide">Recentes</span>
            <button
              type="button"
              onClick={clearRecent}
              className="text-xs text-muted hover:text-dark transition-colors"
            >
              Limpar
            </button>
          </div>
          <ul className="space-y-0.5">
            {recent.map((term) => (
              <li key={term} className="flex items-center group">
                <button
                  type="button"
                  onClick={() => selectRecent(term)}
                  className="flex items-center gap-3 flex-1 px-3 py-2 rounded-xl text-left hover:bg-subtle transition-colors"
                >
                  <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-subtle">
                    <Clock size={14} className="text-muted" />
                  </span>
                  <span className="flex-1 min-w-0 text-sm text-dark truncate">{term}</span>
                </button>
                <button
                  type="button"
                  onClick={() => removeRecent(term)}
                  className="ml-1 p-2 text-muted hover:text-dark opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label={`Eliminar "${term}" das recentes`}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suggestions */}
      <ul className="space-y-0.5 max-h-72 overflow-y-auto hide-scrollbar">
        {results.length > 0 ? (
          results.map((loc) => (
            <li key={loc.id}>
              <button
                type="button"
                onClick={() => handleSelect(loc)}
                className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-left hover:bg-subtle transition-colors group"
              >
                <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-subtle group-hover:bg-border transition-colors">
                  <MapPin size={14} className={colorByType(loc.type)} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-dark truncate">{loc.label}</span>
                  <span className="block text-xs text-muted truncate">{loc.sub}</span>
                </span>
              </button>
            </li>
          ))
        ) : (
          <li className="px-3 py-4 text-sm text-muted text-center">
            Sen resultados para "{query}"
          </li>
        )}
      </ul>
    </div>
  );
}
